<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="isVisible"
        class="detail-backdrop"
        @click.self="$emit('close')"
      >
        <div
          class="detail-container"
          role="dialog"
          aria-modal="true"
          aria-labelledby="battle-detail-title"
        >
          <header class="detail-header">
            <h2 id="battle-detail-title">Combat Log</h2>
            <button
              type="button"
              class="close-btn"
              aria-label="Close combat log"
              @click="$emit('close')"
            >
              ✕
            </button>
          </header>

          <nav class="pane-tabs" aria-label="Combat log sections">
            <button
              type="button"
              :class="{ active: pane === 'summary' }"
              @click="pane = 'summary'"
            >
              Summary
            </button>
            <button
              type="button"
              :class="{ active: pane === 'log' }"
              @click="pane = 'log'"
            >
              Combat Log
            </button>
          </nav>

          <section v-if="pane === 'summary'" class="detail-body">
            <p class="report-victor">{{ victorLine }}</p>
            <p class="report-reason">{{ reasonLine }}</p>
            <p class="meta-line">
              Rounds lasted:
              <strong>{{ roundsLabel }}</strong>
            </p>
            <p v-if="damageLine" class="meta-line">{{ damageLine }}</p>
            <p v-if="gateLine" class="meta-line">{{ gateLine }}</p>

            <div v-if="hasCasualtyBreakdown" class="report-cols">
              <section>
                <h3>{{ friendlyArmyLabel }}</h3>
                <table class="casualty-table">
                  <thead>
                    <tr>
                      <th>Unit Name</th>
                      <th>Starting</th>
                      <th>Lost</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!friendlyRows.length">
                      <td colspan="4">None</td>
                    </tr>
                    <tr v-for="row in friendlyRows" :key="`own-${row.slug}`">
                      <td>
                        {{ row.name }}
                        <span v-if="row.heroXp" class="hero-xp"
                          >+{{ row.heroXp }} Hero XP</span
                        >
                      </td>
                      <td>{{ row.initial }}</td>
                      <td>{{ row.lost }}</td>
                      <td>{{ row.survived }}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
              <section>
                <h3>{{ enemyArmyLabel }}</h3>
                <table class="casualty-table">
                  <thead>
                    <tr>
                      <th>Unit Name</th>
                      <th>Starting</th>
                      <th>Lost</th>
                      <th>Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!enemyRows.length">
                      <td colspan="4">None</td>
                    </tr>
                    <tr v-for="row in enemyRows" :key="`enemy-${row.slug}`">
                      <td>
                        {{ row.name }}
                        <span v-if="row.heroXp" class="hero-xp"
                          >+{{ row.heroXp }} Hero XP</span
                        >
                      </td>
                      <td>{{ row.initial }}</td>
                      <td>{{ row.lost }}</td>
                      <td>{{ row.survived }}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </div>
            <p v-else class="empty-copy">Casualty breakdown unavailable.</p>

            <p v-if="packAwardedLine" class="pack-banner">{{ packAwardedLine }}</p>

            <section v-if="spoilRows.length" class="report-spoils">
              <h3>Spoils looted</h3>
              <ul>
                <li v-for="row in spoilRows" :key="row.key">
                  <i :class="row.icon" :style="{ color: row.color }"></i>
                  {{ row.name }} +{{ row.amount }}
                </li>
              </ul>
            </section>
            <p
              v-else-if="battleResult?.winner === 'attacker'"
              class="empty-copy"
            >
              The vaults were empty.
            </p>

            <section v-if="salvageRows.length" class="report-spoils">
              <h3>Battlefield salvage</h3>
              <ul>
                <li v-for="row in salvageRows" :key="row.key">
                  <i :class="row.icon" :style="{ color: row.color }"></i>
                  {{ row.name }} +{{ row.amount }}
                </li>
              </ul>
            </section>
          </section>

          <section v-else class="detail-body log-body">
            <div class="log-toolbar">
              <div class="filters" role="group" aria-label="Event filters">
                <button
                  v-for="option in filterOptions"
                  :key="option.id"
                  type="button"
                  :class="{ active: eventFilter === option.id }"
                  @click="eventFilter = option.id"
                >
                  {{ option.label }}
                </button>
              </div>
              <div v-if="pageCount > 1" class="block-nav">
                <button
                  type="button"
                  :disabled="pageIndex <= 0"
                  @click="pageIndex -= 1"
                >
                  Prev
                </button>
                <span>{{ pageLabel }}</span>
                <button
                  type="button"
                  :disabled="pageIndex >= pageCount - 1"
                  @click="pageIndex += 1"
                >
                  Next
                </button>
              </div>
            </div>

            <p v-if="!combatEvents.length" class="empty-copy">
              No combat events recorded.
            </p>
            <p v-else-if="!pageEvents.length" class="empty-copy">
              {{ emptyFilterCopy }}
            </p>
            <ul v-else class="event-list">
              <li
                v-for="row in pageEvents"
                :key="row.key"
                :class="row.className"
              >
                {{ row.text }}
              </li>
            </ul>
          </section>

          <details v-if="isDev" class="debug-panel">
            <summary>Raw battle JSON</summary>
            <div class="debug-actions">
              <button type="button" class="copy-btn" @click="copyDebugJson">
                {{ copied ? "Copied" : "Copy" }}
              </button>
            </div>
            <pre>{{ debugJson }}</pre>
          </details>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { BATTLE_DEPLOY_ORDER } from "../utils/battleGrid";
import { resourceDisplay } from "../utils/resourceDisplay";
import { unitDisplay } from "../utils/unitDisplay";

const PAGE_SIZE = 20;
const SLUG_ORDER = [...BATTLE_DEPLOY_ORDER, "hero"];
const isDev = process.env.NODE_ENV !== "production";
const REASON_COPY = {
  gate: "The City Gate was destroyed.",
  attacker_morale: "The attacking army broke and retreated.",
  defender_morale: "The garrison broke. The gate was left defenseless.",
  timeout: "The attackers withdrew when time ran out.",
};
const TERMINATION_COPY = {
  gate_destroyed: "The City Gate was destroyed.",
  morale_broken: "An army's morale broke.",
  wipeout: "An army was wiped out.",
  timeout: "The attackers withdrew when time ran out.",
};

const props = defineProps({
  isVisible: Boolean,
  battleResult: {
    type: Object,
    default: null,
  },
  attackerRace: {
    type: String,
    default: "",
  },
  defenderRace: {
    type: String,
    default: "",
  },
  perspective: {
    type: String,
    default: "attacker",
  },
});

defineEmits(["close"]);

const pane = ref("summary");
const eventFilter = ref("all");
const pageIndex = ref(0);
const copied = ref(false);
let copyTimer = null;

const filterOptions = [
  { id: "all", label: "All" },
  { id: "combat", label: "Combat" },
  { id: "abilities", label: "Abilities Only" },
  { id: "deaths", label: "Deaths" },
  { id: "movement", label: "Movement" },
];

const isDefenderView = computed(
  () => String(props.perspective || "").toLowerCase() === "defender"
);

const victorLine = computed(() => {
  if (!props.battleResult) return "Battle Report";
  const attackerWon = props.battleResult.winner === "attacker";
  if (isDefenderView.value) return attackerWon ? "Defeat" : "Victory";
  return attackerWon ? "Victory" : "Defeat";
});

const reasonLine = computed(() => {
  const termination = props.battleResult?.summary?.terminationReason;
  if (termination && TERMINATION_COPY[termination]) {
    return TERMINATION_COPY[termination];
  }
  return (
    REASON_COPY[props.battleResult?.reason] || "The battle has ended."
  );
});

const roundsLabel = computed(() => {
  const rounds = props.battleResult?.summary?.rounds;
  return Number.isFinite(Number(rounds)) ? Number(rounds) : "N/A";
});

const damageLine = computed(() => {
  const damage = props.battleResult?.summary?.damage;
  if (!damage) return "";
  const atk = Math.max(0, Math.floor(Number(damage.attacker) || 0));
  const def = Math.max(0, Math.floor(Number(damage.defender) || 0));
  if (atk < 1 && def < 1) return "";
  const you = isDefenderView.value ? def : atk;
  const enemy = isDefenderView.value ? atk : def;
  return `Damage dealt: You ${you} · Enemy ${enemy}`;
});

const gateLine = computed(() => {
  const gate = props.battleResult?.summary?.gate;
  if (!gate) return "";
  const initial = Math.max(0, Math.floor(Number(gate.initialHp) || 0));
  const remaining = Math.max(0, Math.floor(Number(gate.remainingHp) || 0));
  if (initial < 1 && remaining < 1) return "";
  return `City Gate HP: ${remaining}/${initial}`;
});

function casualtyRows(side, race) {
  const map = props.battleResult?.summary?.casualties?.[side] || {};
  const slugs = [
    ...SLUG_ORDER.filter((slug) => map[slug]),
    ...Object.keys(map).filter((slug) => !SLUG_ORDER.includes(slug)),
  ];
  const gained = props.battleResult?.heroXpGained;
  const xp =
    gained && gained.side === side
      ? Math.max(0, Math.floor(Number(gained.xp) || 0))
      : 0;
  return slugs.map((slug) => {
    const row = map[slug] || {};
    const name =
      row.label ||
      (slug === "hero" ? "Hero" : unitDisplay(slug, race).name);
    return {
      slug,
      name,
      heroXp: slug === "hero" && xp > 0 ? xp : 0,
      initial: Math.max(0, Math.floor(Number(row.initial) || 0)),
      lost: Math.max(0, Math.floor(Number(row.lost) || 0)),
      survived: Math.max(0, Math.floor(Number(row.survived) || 0)),
    };
  });
}

const attackerRows = computed(() =>
  casualtyRows("attacker", props.attackerRace)
);
const defenderRows = computed(() =>
  casualtyRows("defender", props.defenderRace)
);

const friendlyRows = computed(() =>
  isDefenderView.value ? defenderRows.value : attackerRows.value
);
const enemyRows = computed(() =>
  isDefenderView.value ? attackerRows.value : defenderRows.value
);
const friendlyArmyLabel = computed(() => "Your army");
const enemyArmyLabel = computed(() => "Enemy army");

const hasCasualtyBreakdown = computed(
  () => attackerRows.value.length > 0 || defenderRows.value.length > 0
);

const spoilRows = computed(() => {
  const spoils = props.battleResult?.spoils || {};
  return ["gold", "food", "wood", "stone", "iron", "gems"]
    .map((key) => {
      const amount = Math.max(0, Math.floor(Number(spoils[key]) || 0));
      if (amount < 1) return null;
      return { key, amount, ...resourceDisplay(key) };
    })
    .filter(Boolean);
});

const salvageRows = computed(() => {
  const salvage = props.battleResult?.salvage || {};
  return ["food", "wood", "iron", "essence"]
    .map((key) => {
      const amount = Math.max(0, Math.floor(Number(salvage[key]) || 0));
      if (amount < 1) return null;
      return { key, amount, ...resourceDisplay(key) };
    })
    .filter(Boolean);
});

const packAwardedLine = computed(() => {
  const awarded = props.battleResult?.packAwarded;
  if (!awarded) return "";
  const name =
    typeof awarded === "object"
      ? awarded.name || "Core Pack"
      : "Core Pack";
  return `${name} recovered from the field.`;
});

const combatEvents = computed(() =>
  Array.isArray(props.battleResult?.combatEvents)
    ? props.battleResult.combatEvents
    : []
);

function eventRound(event) {
  return Math.floor(Number(event?.round ?? event?.tick) || 0);
}

function matchesFilter(event) {
  const type = event?.type;
  if (eventFilter.value === "combat") return type === "attack";
  if (eventFilter.value === "abilities") return type === "ability";
  if (eventFilter.value === "deaths") return type === "death";
  if (eventFilter.value === "movement") return type === "move";
  return (
    type === "attack" ||
    type === "ability" ||
    type === "death" ||
    type === "milestone" ||
    type === "move"
  );
}

const filteredEvents = computed(() =>
  combatEvents.value.filter(matchesFilter)
);

const pageCount = computed(() =>
  Math.max(1, Math.ceil((filteredEvents.value.length || 0) / PAGE_SIZE))
);

const pageStart = computed(() => pageIndex.value * PAGE_SIZE);
const pageEnd = computed(() =>
  Math.min(filteredEvents.value.length, pageStart.value + PAGE_SIZE)
);

const pageLabel = computed(() => {
  const total = filteredEvents.value.length;
  if (total < 1) return "Page 1 of 1";
  return `Page ${pageIndex.value + 1} of ${pageCount.value} · Events ${
    pageStart.value + 1
  }–${pageEnd.value} of ${total}`;
});

function actorName(event) {
  if (event?.actorKind === "gate" || event?.actorSlug === "gate") {
    return "City Gate";
  }
  if (event?.actor) return event.actor;
  const slug = String(event?.actorSlug || "").toLowerCase();
  if (event?.actorKind === "hero" || slug === "hero") return "Hero";
  if (slug) {
    const race =
      event?.side === "defender" ? props.defenderRace : props.attackerRace;
    return unitDisplay(slug, race).name;
  }
  return "Unit";
}

function targetName(event) {
  if (event?.targetKind === "gate" || event?.targetSlug === "gate") {
    return "City Gate";
  }
  if (event?.target) return event.target;
  const slug = String(event?.targetSlug || "").toLowerCase();
  if (event?.targetKind === "hero" || slug === "hero") return "Hero";
  if (slug) {
    const race =
      event?.side === "attacker" ? props.defenderRace : props.attackerRace;
    return unitDisplay(slug, race).name;
  }
  return "Unknown";
}

function cellLabel(point) {
  if (!point || typeof point !== "object") return null;
  const row = Math.floor(Number(point.row ?? point.r));
  const col = Math.floor(Number(point.col ?? point.c));
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  return `(${row}, ${col})`;
}

function formatEvent(event, index) {
  const round = eventRound(event) || "?";
  const prefix = `[Round ${round}]`;
  if (event?.type === "attack") {
    const actor = actorName(event);
    const target = targetName(event);
    const damage = Math.max(0, Math.floor(Number(event.damage) || 0));
    let text = `${prefix} ${actor} struck ${target} for ${damage} damage`;
    const isGate = event.targetKind === "gate" || event.targetSlug === "gate";
    if (isGate && event.targetHp != null && event.targetHpMax != null) {
      text += ` (Gate HP: ${event.targetHp}/${event.targetHpMax})`;
    }
    const casualties = Math.max(0, Math.floor(Number(event.casualties) || 0));
    if (casualties > 0 && !isGate) {
      const remaining = Math.max(0, Math.floor(Number(event.remaining) || 0));
      text += ` (${casualties} casualties, ${remaining} remaining)`;
    }
    return {
      key: `${event.actorId || "atk"}-${round}-${index}`,
      text,
      className: "event-attack",
    };
  }
  if (event?.type === "death") {
    return {
      key: `${event.actorId || "death"}-${round}-${index}`,
      text: `${prefix} ${actorName(event)} was slain.`,
      className: "event-death",
    };
  }
  if (event?.type === "ability") {
    const name = event?.ability?.name || "Ability";
    const damage = Math.max(0, Math.floor(Number(event.damage) || 0));
    const amount = event?.ability?.amount;
    const extra =
      amount != null ? ` (${amount})` : damage ? ` dealing ${damage} damage` : "";
    return {
      key: `${event.actorId || "ability"}-${round}-${index}`,
      text: `${prefix} ${actorName(event)} triggered [${name}]${extra}`,
      className: "event-ability",
    };
  }
  if (event?.type === "milestone") {
    const copy =
      TERMINATION_COPY[event?.terminationReason] || "The battle has ended.";
    return {
      key: `milestone-${round}-${index}`,
      text: `${prefix} ${copy}`,
      className: "event-milestone",
    };
  }
  if (event?.type === "move") {
    const from = cellLabel(event.from) || "?";
    const to = cellLabel(event.to) || "?";
    return {
      key: `${event.actorId || "move"}-${round}-${from}-${to}-${index}`,
      text: `${prefix} ${actorName(event)} moved from ${from} to ${to}`,
      className: "event-move",
    };
  }
  return null;
}

const pageEvents = computed(() =>
  filteredEvents.value
    .slice(pageStart.value, pageEnd.value)
    .map((event, index) => formatEvent(event, pageStart.value + index))
    .filter(Boolean)
);

const emptyFilterCopy = computed(() => {
  if (eventFilter.value === "abilities") return "No ability events this battle.";
  if (eventFilter.value === "deaths") return "No death events this battle.";
  if (eventFilter.value === "movement") return "No movement events this battle.";
  if (eventFilter.value === "combat") return "No combat events this battle.";
  return "No combat events recorded.";
});

const debugJson = computed(() => {
  if (!isDev) return "";
  return JSON.stringify(
    {
      summary: props.battleResult?.summary ?? null,
      combatEvents: Array.isArray(props.battleResult?.combatEvents)
        ? props.battleResult.combatEvents
        : [],
    },
    null,
    2
  );
});

async function copyDebugJson() {
  if (!isDev) return;
  try {
    await navigator.clipboard.writeText(debugJson.value);
    copied.value = true;
    if (copyTimer) clearTimeout(copyTimer);
    copyTimer = setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    copied.value = false;
  }
}

function resetView() {
  pane.value = "summary";
  eventFilter.value = "all";
  pageIndex.value = 0;
  copied.value = false;
}

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) resetView();
  }
);

watch(eventFilter, () => {
  pageIndex.value = 0;
});

watch(pageCount, (count) => {
  if (pageIndex.value > count - 1) {
    pageIndex.value = Math.max(0, count - 1);
  }
});
</script>

<style scoped>
.detail-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 0.75rem;
}

.detail-container {
  width: min(920px, 96vw);
  max-height: min(90vh, 90dvh);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: #0a0a14;
  border: 1px solid #00f6ff;
  border-radius: 10px;
  box-shadow: 0 0 30px rgba(0, 246, 255, 0.5);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0, 246, 255, 0.3);
  flex-shrink: 0;
}

.detail-header h2 {
  margin: 0;
  font-family: "Orbitron", sans-serif;
  color: #00f6ff;
  font-size: 1.1rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #a9b1d6;
  cursor: pointer;
  padding: 0 0.35rem;
  line-height: 1;
}

.close-btn:hover {
  color: #ff00ff;
}

.pane-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem 0;
  flex-shrink: 0;
}

.pane-tabs button,
.filters button,
.block-nav button,
.copy-btn {
  background: transparent;
  border: 1px solid #556c88;
  color: #a9b1d6;
  border-radius: 999px;
  padding: 0.28rem 0.8rem;
  cursor: pointer;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.pane-tabs button.active,
.filters button.active {
  border-color: #00f6ff;
  color: #00f6ff;
  box-shadow: 0 0 10px rgba(0, 246, 255, 0.35);
}

.detail-body {
  padding: 1rem 1.25rem;
  color: #c0c0c0;
  line-height: 1.55;
  overflow-y: auto;
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.report-victor {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: 1.4rem;
  color: #f4e6c4;
  text-align: center;
}

.report-reason,
.meta-line {
  margin: 0;
  text-align: center;
  color: #88c0d0;
}

.report-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.detail-body h3 {
  margin: 0 0 0.4rem;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9b48a;
}

.casualty-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.casualty-table th,
.casualty-table td {
  padding: 0.28rem 0.35rem;
  text-align: left;
  border-bottom: 1px solid rgba(0, 246, 255, 0.12);
}

.casualty-table th:not(:first-child),
.casualty-table td:not(:first-child) {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.empty-copy {
  margin: 0;
  color: #88c0d0;
  text-align: center;
}

.report-spoils ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.report-spoils li {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.hero-xp {
  margin-left: 0.4rem;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  color: #e6c35c;
}

.pack-banner {
  margin: 0.85rem 0 0;
  padding: 0.55rem 0.75rem;
  border: 1px solid rgba(214, 158, 46, 0.55);
  background: rgba(214, 158, 46, 0.12);
  color: #e6c35c;
  font-size: 0.88rem;
  letter-spacing: 0.04em;
  text-align: center;
}

.log-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.65rem;
  align-items: center;
}

.filters,
.block-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.block-nav span {
  font-size: 0.8rem;
  color: #88c0d0;
}

.event-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  font-size: 0.86rem;
  color: #d8dee9;
}

.event-move {
  color: #7b88a8;
  font-size: 0.8rem;
}

.event-death {
  color: #e8a0a0;
}

.event-milestone {
  color: #c9b48a;
}

.debug-panel {
  flex-shrink: 0;
  border-top: 1px solid rgba(0, 246, 255, 0.3);
  padding: 0.65rem 1.25rem 0.9rem;
  color: #a9b1d6;
}

.debug-panel summary {
  cursor: pointer;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.debug-actions {
  display: flex;
  justify-content: flex-end;
  margin: 0.4rem 0;
}

.debug-panel pre {
  margin: 0;
  max-height: 180px;
  overflow: auto;
  font-size: 0.72rem;
  background: rgba(0, 0, 0, 0.35);
  padding: 0.6rem;
  border-radius: 6px;
  color: #c0c0c0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 720px) {
  .report-cols {
    grid-template-columns: 1fr;
  }
}
</style>
